import { useMutation } from '@apollo/client';
import type { UseEditProviderAllowedAccounts } from '@openx/components-provider/lib/utils/context/ProviderHookTypes.ts';
import type { AllowedAccountData } from '@openx/types/provider.ts';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { LinkOrganizationToProviderDocument } from '../../../graphql/mutation/providers/LinkOrganizationToProvider.generated.tsx';
import { UnlinkOrganizationFromProviderDocument } from '../../../graphql/mutation/providers/UnlinkOrganizationFromProvider.generated.tsx';

export const useEditProviderAllowedAccounts: UseEditProviderAllowedAccounts = () => {
  const { t } = useTranslation();
  const [linkOrganizationToProviderMutation] = useMutation(LinkOrganizationToProviderDocument);
  const [unlinkOrganizationFromProviderMutation] = useMutation(UnlinkOrganizationFromProviderDocument);
  const { enqueueSnackbar } = useSnackbar();

  const linkOrganizationsToProvider = useCallback(
    async (accountsToLink: AllowedAccountData[]) => {
      try {
        await linkOrganizationToProviderMutation({
          variables: {
            objects: accountsToLink,
          },
        });
      } catch (e) {
        enqueueSnackbar(t('Error linking organizations to provider'), { variant: 'error' });
        // biome-ignore lint/suspicious/noConsole: logging error in console
        console.error('Error linking organizations to provider', e);
      }
    },
    [enqueueSnackbar, linkOrganizationToProviderMutation, t]
  );

  const unlinkOrganizationsFromProvider = useCallback(
    async (accountsToUnlink: AllowedAccountData[]) => {
      const promises = accountsToUnlink.map(account => {
        return unlinkOrganizationFromProviderMutation({
          variables: { account_id: account.account_id, provider_id: account.provider_id },
        });
      });

      try {
        await Promise.all(promises);
      } catch (error) {
        enqueueSnackbar(t('Error unlinking organizations from provider'), { variant: 'error' });
        // biome-ignore lint/suspicious/noConsole: logging error in console
        console.error('Error unlinking organizations from provider', error);
      }
    },
    [enqueueSnackbar, t, unlinkOrganizationFromProviderMutation]
  );

  return {
    linkOrganizationsToProvider,
    unlinkOrganizationsFromProvider,
  };
};
